// Shared utilities for Automated Report Subscriptions feature

import { z } from 'zod';
import type * as Types from '../../graphql/types';

// GraphQL utility to strip __typename from objects for use in input types
export const stripTypename = <T>(obj: T & { __typename?: string }): Omit<T, '__typename'> => {
  if (obj && typeof obj === 'object') {
    const { __typename, ...rest } = obj;
    return rest;
  }
  return obj as Omit<T, '__typename'>;
};

// Convert GraphQL preferences type to input type (strips __typename)
export const preferencesToInput = (
  preferences: Types.ReportSubscriptionPreferences,
): Types.ReportSubscriptionPreferencesInput => {
  return stripTypename(preferences);
};

// Validation error type
export interface ValidationErrors {
  [key: string]: string;
}

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_FORMAT_REGEX = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;

const OptionalIsoDateString = z.preprocess((value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  return value;
}, z.string().regex(ISO_DATE_REGEX, 'Date must be in YYYY-MM-DD format').optional());

const OptionalString = z.preprocess((value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string' && value.trim() === '') return undefined;
  return value;
}, z.string().optional());

const OptionalStringArray = z.preprocess((value) => {
  if (value === null || value === undefined) return undefined;
  if (Array.isArray(value) && value.length === 0) return undefined;
  return value;
}, z.array(z.string()).optional());

export const ReportSubscriptionPreferencesSchema = z
  .object({
    includeMonday: z.boolean(),
    includeTuesday: z.boolean(),
    includeWednesday: z.boolean(),
    includeThursday: z.boolean(),
    includeFriday: z.boolean(),
    includeSaturday: z.boolean(),
    includeSunday: z.boolean(),
    deliveryTime: z.string().regex(TIME_FORMAT_REGEX, 'Delivery time is required'),
    useAiNotes: z.boolean(),
    description: OptionalString,
    recipientTeamMemberIds: OptionalStringArray,
    startDate: OptionalIsoDateString,
    endDate: OptionalIsoDateString,
    message: OptionalString,
    priority: z.number().min(0).max(10).default(0),
  })
  .superRefine((preferences, context) => {
    const hasAnySelectedDay =
      preferences.includeMonday ||
      preferences.includeTuesday ||
      preferences.includeWednesday ||
      preferences.includeThursday ||
      preferences.includeFriday ||
      preferences.includeSaturday ||
      preferences.includeSunday;

    if (!hasAnySelectedDay) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one day must be selected',
        path: ['days'],
      });
    }

    if (
      preferences.startDate &&
      preferences.endDate &&
      preferences.startDate > preferences.endDate
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date must be on or before end date',
        path: ['dateRange'],
      });
    }
  });

// Common validation functions
export const validateBrandCode = (brandCode: string): string | null => {
  if (!brandCode.trim()) {
    return 'Brand code is required';
  }
  if (!/^[A-Z0-9]{5}$/.test(brandCode)) {
    return 'Brand code must be 5 characters (letters and numbers only)';
  }
  return null;
};

export const validateTimezone = (timezone: string): string | null => {
  if (!timezone) {
    return 'Timezone is required';
  }
  return null;
};

export const validatePreferences = (
  preferences: Types.ReportSubscriptionPreferences | Types.ReportSubscriptionPreferencesInput,
): string | null => {
  const hasAnySelectedDay =
    preferences.includeMonday ||
    preferences.includeTuesday ||
    preferences.includeWednesday ||
    preferences.includeThursday ||
    preferences.includeFriday ||
    preferences.includeSaturday ||
    preferences.includeSunday;

  if (!hasAnySelectedDay) {
    return 'At least one day must be selected';
  }
  return null;
};

// Validation runner
export const runValidations = (validators: (() => string | null)[]): ValidationErrors => {
  const errors: ValidationErrors = {};

  validators.forEach((validator, index) => {
    const error = validator();
    if (error) {
      // Use a generic key or pass in specific keys
      errors[`field_${index}`] = error;
    }
  });

  return errors;
};

// Form validation for subscription form
export const validateSubscriptionForm = (
  brandCode: string,
  timezone: string,
  preferences: Types.ReportSubscriptionPreferences | Types.ReportSubscriptionPreferencesInput,
  includeBrandCodeValidation: boolean = true,
): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (includeBrandCodeValidation) {
    const brandCodeError = validateBrandCode(brandCode);
    if (brandCodeError) errors.brandCode = brandCodeError;
  }

  const timezoneError = validateTimezone(timezone);
  if (timezoneError) errors.timezone = timezoneError;

  const prefResult = ReportSubscriptionPreferencesSchema.safeParse(preferences);
  if (!prefResult.success) {
    for (const issue of prefResult.error.issues) {
      const key = String(issue.path[0] ?? '');

      if (key === 'days') {
        errors.days = issue.message;
        continue;
      }

      if (key === 'deliveryTime') {
        errors.deliveryTime = issue.message;
        continue;
      }

      if (key === 'startDate' || key === 'endDate' || key === 'dateRange') {
        errors.dateRange = issue.message;
      }
    }
  }

  return errors;
};

// Async operation handler with consistent error handling
export interface AsyncOperationResult<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
}

export const handleAsyncOperation = async <T>(
  operation: () => Promise<T>,
  errorMessage: string,
): Promise<AsyncOperationResult<T>> => {
  try {
    const result = await operation();
    return {
      success: true,
      data: result,
    };
  } catch (error) {
    console.error(errorMessage, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// Form error handling helpers
export const clearFieldError = (
  errors: ValidationErrors,
  setErrors: (errors: ValidationErrors) => void,
  fieldName: string,
) => {
  if (errors[fieldName]) {
    const newErrors = { ...errors };
    delete newErrors[fieldName];
    setErrors(newErrors);
  }
};

export const clearRelatedErrors = (
  errors: ValidationErrors,
  setErrors: (errors: ValidationErrors) => void,
  fieldNames: string[],
) => {
  const hasAnyError = fieldNames.some((field) => errors[field]);
  if (hasAnyError) {
    const newErrors = { ...errors };
    for (const field of fieldNames) {
      delete newErrors[field];
    }
    setErrors(newErrors);
  }
};

// Day formatting utility (used in components)
export const formatSelectedDays = (
  preferences: Types.ReportSubscriptionPreferences | Types.ReportSubscriptionPreferencesInput,
): string => {
  const days: string[] = [];

  if (preferences.includeMonday) days.push('Monday');
  if (preferences.includeTuesday) days.push('Tuesday');
  if (preferences.includeWednesday) days.push('Wednesday');
  if (preferences.includeThursday) days.push('Thursday');
  if (preferences.includeFriday) days.push('Friday');
  if (preferences.includeSaturday) days.push('Saturday');
  if (preferences.includeSunday) days.push('Sunday');

  if (days.length === 0) return 'No days selected';
  if (days.length === 1) return days[0];
  if (days.length === 2) return `${days[0]} & ${days[1]}`;
  return days.join(', ');
};

// Size class generator (used in toggle component)
export const generateSizeClasses = (size: 'sm' | 'md' | 'lg') => {
  switch (size) {
    case 'sm':
      return {
        container: 'flex items-center space-x-2',
        label: 'text-sm',
        button: 'h-6 w-6 p-0',
        icon: 'h-3 w-3',
      };
    case 'lg':
      return {
        container: 'flex items-center justify-between space-x-4',
        label: 'text-base font-medium',
        button: 'h-8 w-8 p-0',
        icon: 'h-4 w-4',
      };
    default: // md
      return {
        container: 'flex items-center space-x-3',
        label: 'text-sm font-medium',
        button: 'h-7 w-7 p-0',
        icon: 'h-3.5 w-3.5',
      };
  }
};
